import { useQuery } from "@tanstack/react-query";
import type { OutlineNode, OutlineView as OutlineViewData, ProjectDetail, StoryboardView } from "@dramapie/shared";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError, getOutline, getProject } from "../../lib/api";
import { formatClock } from "../../lib/format";
import {
  OUTLINE_LEVEL_LABEL,
  charCount,
  defaultOutlineNode,
  findOutlineNode,
  outlinePath,
} from "../../lib/world";
import { ErrorState, LoadingState } from "../states";

/**
 * 大纲（O-01 ~ O-03）。
 *
 * 数据来自后端 M7 真模型 `OutlineView`：`root` 递归渲染，每级显示 `outline` 正文
 * （不是一句摘要）。没有这份文档时给“用 Agent 生成”的空态，不造占位树。
 */
export interface OutlineViewProps {
  projectId?: string;
  project?: ProjectDetail | null;
  /** 直接给数据（测试 / 父级已取数）；不给就自己按 projectId 取。 */
  outline?: OutlineViewData | null;
  /** 当前集故事板（只用于「本话场次」与统计，可省）。 */
  storyboard?: StoryboardView | null;
  /** 打开剧本 / 分镜稿。 */
  onOpenScript?: (episodeId?: string) => void;
  /** 「在故事板打开」。 */
  onOpenStoryboard?: () => void;
  /** 产出线卡片（O-03）；没接线时只展示。 */
  onOpenOutput?: (outputId: string) => void;
  /** 空态的「用 Agent 生成」。 */
  onGenerate?: () => void;
}

export function OutlineView({
  projectId,
  project: projectProp,
  outline: outlineProp,
  storyboard = null,
  onOpenScript,
  onOpenStoryboard,
  onOpenOutput,
  onGenerate,
}: OutlineViewProps) {
  const params = useParams<{ projectId: string }>();
  const pid = projectId ?? params.projectId ?? "";

  const projectQuery = useQuery({
    queryKey: ["project", pid],
    queryFn: () => getProject(pid),
    enabled: projectProp === undefined && pid.length > 0,
    staleTime: 30_000,
    retry: false,
  });
  const project = projectProp ?? projectQuery.data ?? null;

  const outlineQuery = useQuery({
    queryKey: ["outline", pid],
    queryFn: () => getOutline(pid),
    enabled: outlineProp === undefined && pid.length > 0,
    staleTime: 30_000,
    retry: false,
  });

  const outline =
    outlineProp !== undefined ? outlineProp : (outlineQuery.data ?? null);
  const root = outline?.root ?? null;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [closedIds, setClosedIds] = useState<ReadonlySet<string>>(new Set());
  const [openAncestors, setOpenAncestors] = useState<ReadonlySet<string>>(new Set());

  // 根换了：选中项失效就回到第一个有正文的集级节点。
  useEffect(() => {
    setSelectedId((current) =>
      current && findOutlineNode(root, current) ? current : (defaultOutlineNode(root)?.id ?? null),
    );
  }, [root]);

  const path = useMemo(() => outlinePath(root, selectedId), [root, selectedId]);
  const node = path.length > 0 ? (path[path.length - 1] ?? null) : null;
  const ancestors = path.slice(0, -1);
  const childCount = node?.children.length ?? 0;

  const missing =
    outlineQuery.isError &&
    (outlineQuery.error instanceof ApiError
      ? outlineQuery.error.status === 404
      : false);

  const loading =
    outlineProp === undefined && outlineQuery.isPending && pid.length > 0;

  function toggle(id: string) {
    setClosedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAncestor(id: string) {
    setOpenAncestors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function renderTree(item: OutlineNode, depth: number): React.ReactNode {
    const kids = item.children;
    const active = selectedId === item.id;
    const isOpen = !closedIds.has(item.id);
    const level = Math.min(depth, 3);
    if (kids.length > 0) {
      return (
        <div
          key={item.id}
          className={`dp-ol-branch ${active ? "is-active" : ""} ${isOpen ? "is-open" : ""}`}
        >
          <div className="dp-ol-row">
            <button
              type="button"
              className="dp-ol-caret"
              aria-label={`${isOpen ? "收起" : "展开"} ${item.title}`}
              aria-expanded={isOpen}
              onClick={() => toggle(item.id)}
            />
            <button
              type="button"
              className="dp-ol-title"
              aria-current={active ? "true" : undefined}
              onClick={() => setSelectedId(item.id)}
            >
              {item.title}
            </button>
            <small>{OUTLINE_LEVEL_LABEL[level]}</small>
          </div>
          {isOpen ? (
            <div className="dp-ol-children">
              {kids.map((child) => renderTree(child, depth + 1))}
            </div>
          ) : null}
        </div>
      );
    }
    return (
      <button
        key={item.id}
        type="button"
        className={`dp-ol-leaf ${active ? "is-active" : ""}`}
        aria-current={active ? "true" : undefined}
        onClick={() => setSelectedId(item.id)}
      >
        <i className={`dp-ol-dot ${depth >= 3 ? "is-current" : ""}`} aria-hidden="true" />
        <span>{item.title}</span>
        {item.outline.trim().length === 0 ? <small>待补</small> : null}
      </button>
    );
  }

  if (loading) {
    return (
      <div className="dp-doc-layout">
        <div className="dp-doc-loading">
          <LoadingState label="正在读取大纲…" />
        </div>
      </div>
    );
  }

  if (outlineQuery.isError && !missing) {
    return (
      <div className="dp-doc-layout">
        <div className="dp-doc-loading">
          <ErrorState
            title="大纲加载失败"
            message="读取 outline/outline.json 的投影失败，请重试。"
            onRetry={() => void outlineQuery.refetch()}
          />
        </div>
      </div>
    );
  }

  if (!root) {
    return (
      <div className="dp-doc-empty">
        <p className="dp-doc-empty-title">还没有大纲</p>
        <p className="dp-doc-empty-text">
          大纲来自 <code>outline/outline.json</code>（卷 → 单元 → 集，每级真实正文）。
        </p>
        {onGenerate ? (
          <button type="button" className="dp-btn sm primary" onClick={() => onGenerate()}>
            用 Agent 生成
          </button>
        ) : null}
      </div>
    );
  }

  const body = node?.outline ?? "";
  const level = path.length;

  return (
    <div className="dp-doc-layout dp-outline-view">
      <aside className="dp-doc-side" aria-label="大纲树">
        <div className="dp-doc-side-head">
          <span>大纲</span>
          <span>{outline ? `rev ${outline.rev}` : ""}</span>
        </div>
        <div className="dp-ol-tree">{renderTree(root, 1)}</div>
      </aside>

      <main className="dp-doc-main">
        <header className="dp-doc-head">
          <div className="dp-ep-eyebrow">大纲 · 树状视图</div>
          <h1 className="dp-doc-title">{node?.title ?? "大纲"}</h1>
          <p className="dp-doc-sub">
            从卷到单元再到集；每一级的正文都来自 outline.json，不是一句摘要。
          </p>
          <div className="dp-doc-actions">
            {onOpenScript ? (
              <button type="button" className="dp-btn" onClick={() => onOpenScript()}>
                打开剧本 / 分镜稿
              </button>
            ) : null}
            {onOpenStoryboard ? (
              <button type="button" className="dp-btn ghost" onClick={() => onOpenStoryboard()}>
                在故事板打开
              </button>
            ) : null}
          </div>
        </header>

        <div className="dp-doc-meta">
          <span>
            <b>{charCount(body)}</b> 字正文
          </span>
          <span>
            <b>{childCount}</b> 子节点
          </span>
          <span>
            <b>{OUTLINE_LEVEL_LABEL[Math.min(level, 3)] ?? "节点"}</b>
          </span>
          {storyboard ? (
            <span>
              <b>{storyboard.stats.shotCount}</b> 镜 ·{" "}
              <b>{formatClock(storyboard.shots.reduce((sum, shot) => sum + shot.duration, 0))}</b>
            </span>
          ) : null}
        </div>

        <section className="dp-doc-section dp-outline-text-section">
          <h3>
            分级大纲 <span>卷 / 单元 / 集 · 正文</span>
          </h3>
          <div className="dp-outline-paper">
            <div className="dp-outline-paper-head">
              <span>
                L{Math.min(level, 3)} · {OUTLINE_LEVEL_LABEL[Math.min(level, 3)] ?? "节点"}
              </span>
              <b>{node?.title ?? "大纲"}</b>
              <em>约 {charCount(body)} 字</em>
            </div>
            {body.trim().length > 0 ? (
              <p className="dp-outline-body">{body}</p>
            ) : (
              <p className="dp-tiny-note">这一级还没有正文；后端补 outline 后自动显示。</p>
            )}
          </div>

          {ancestors
            .slice()
            .reverse()
            .map((ancestor) => {
              const open = openAncestors.has(ancestor.id);
              return (
                <div key={ancestor.id} className="dp-outline-up">
                  <button
                    type="button"
                    className="dp-outline-up-head"
                    aria-expanded={open}
                    aria-label={`展开上级 ${ancestor.title}`}
                    onClick={() => toggleAncestor(ancestor.id)}
                  >
                    <span aria-hidden="true">{open ? "▾" : "▸"}</span>
                    {ancestor.title}
                    <em>约 {charCount(ancestor.outline)} 字</em>
                  </button>
                  {open ? <p>{ancestor.outline || "这一级还没有正文。"}</p> : null}
                </div>
              );
            })}
        </section>

        <section className="dp-doc-section">
          <h3>
            关联产出 <span>同一话在各条产出线里的位置</span>
          </h3>
          {(project?.outputs ?? []).length > 0 ? (
            <div className="dp-rel-grid">
              {(project?.outputs ?? []).map((output, index) => (
                <button
                  key={output.id}
                  type="button"
                  className={`dp-rel-card ${index === 0 ? "is-current" : ""}`}
                  aria-label={`产出线 ${output.title}`}
                  onClick={() => onOpenOutput?.(output.id)}
                >
                  <span className="dp-rel-mark" aria-hidden="true">
                    {output.title.slice(0, 1)}
                  </span>
                  <span className="dp-rel-copy">
                    <b>{output.title}</b>
                    <span>{index === 0 ? "当前" : "产出线"}</span>
                  </span>
                  <span className="dp-rel-arrow" aria-hidden="true">
                    →
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="dp-tiny-note">项目还没有产出线。</p>
          )}
        </section>

        {storyboard && storyboard.scenes.length > 0 ? (
          <section className="dp-doc-section">
            <h3>
              本话场次 <span>点一场，跳到故事板对应位置</span>
            </h3>
            <div className="dp-scene-chips">
              {[...storyboard.scenes]
                .sort((a, b) => a.order - b.order)
                .map((scene) => (
                  <button
                    key={scene.id}
                    type="button"
                    aria-label={`场次 ${scene.name}`}
                    onClick={() => onOpenStoryboard?.()}
                  >
                    {scene.name}
                    <span>{scene.shotIds.length} 镜</span>
                  </button>
                ))}
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
}
